/**
 * Точки привязки («anchor points») модуля в плане — à la Bazis-Мебельщик.
 *
 * A module footprint has 9 plan anchors: 4 corners, 4 edge midpoints and the
 * centre. During interactive placement (drag / numeric entry) the ACTIVE
 * anchor is the point that follows the cursor and is the point that snaps to
 * the grid / neighbours. The scene data model stays min-corner (offsets from
 * the module's base AABB) — this module only converts between the anchor
 * point and the min-corner / centre on the frontend.
 *
 * Pure and dependency-free (no THREE, no React) so the rotation math is
 * unit-testable. All coordinates are millimetres in the world XZ plane
 * (three.js: Y up, plan = XZ).
 *
 * Plan convention (floor-plan viewed from above, +X right, +Z toward the
 * viewer / module front):
 *   - `t*` (top row of the plan)    = back  (min Z)
 *   - `b*` (bottom row of the plan) = front (max Z)
 *   - `*l` = left (min X), `*r` = right (max X), `m`/`c` = middles/centre.
 *
 * Vertical (floor/top) anchor levels are intentionally NOT modelled here —
 * module placement translates in the XZ plane only (Y passes through
 * unchanged), so plan anchors are sufficient.
 */

/** Row-major plan grid: front row, middle row, back row. */
export const ANCHOR_POINT_IDS = ['bl', 'bm', 'br', 'ml', 'c', 'mr', 'tl', 'tm', 'tr'] as const;

export type AnchorPointId = (typeof ANCHOR_POINT_IDS)[number];

/** Canonical default anchor used when a module has no explicit active anchor. */
export const DEFAULT_ANCHOR_POINT: AnchorPointId = 'c';

/** Local (unrotated) footprint size in plan, millimetres. */
export type PlanSize = { w: number; d: number };

/** A point/vector in the world XZ plane, millimetres. */
export type PlanPoint = { x: number; z: number };

/**
 * Fractional position of an anchor on the LOCAL (unrotated) footprint,
 * measured from the local min-corner: fx along width (0 = left, 1 = right),
 * fz along depth (0 = back / min Z, 1 = front / max Z).
 */
const ANCHOR_FRACTIONS: Record<AnchorPointId, { fx: number; fz: number }> = {
  tl: { fx: 0, fz: 0 },
  tm: { fx: 0.5, fz: 0 },
  tr: { fx: 1, fz: 0 },
  ml: { fx: 0, fz: 0.5 },
  c: { fx: 0.5, fz: 0.5 },
  mr: { fx: 1, fz: 0.5 },
  bl: { fx: 0, fz: 1 },
  bm: { fx: 0.5, fz: 1 },
  br: { fx: 1, fz: 1 },
};

/** Short RU labels for the HUD chip («Привязка: …»). */
export const ANCHOR_LABEL_RU: Record<AnchorPointId, string> = {
  bl: 'ЛП угол',
  bm: 'перед, середина',
  br: 'ПП угол',
  ml: 'левая середина',
  c: 'центр',
  mr: 'правая середина',
  tl: 'ЛЗ угол',
  tm: 'зад, середина',
  tr: 'ПЗ угол',
};

/** Full RU labels (tooltips). */
export const ANCHOR_TITLE_RU: Record<AnchorPointId, string> = {
  bl: 'Левый передний угол',
  bm: 'Середина переднего края',
  br: 'Правый передний угол',
  ml: 'Середина левого края',
  c: 'Центр модуля',
  mr: 'Середина правого края',
  tl: 'Левый задний угол',
  tm: 'Середина заднего края',
  tr: 'Правый задний угол',
};

export function isAnchorPointId(value: unknown): value is AnchorPointId {
  return typeof value === 'string' && (ANCHOR_POINT_IDS as readonly string[]).includes(value);
}

/**
 * cos/sin of a yaw angle with EXACT values at multiples of 90° so that the
 * offsets of axis-aligned rotations carry no floating-point dust (corners of
 * a 90°-rotated box land exactly on the rotated AABB's corners).
 */
function cosSinDeg(deg: number): { c: number; s: number } {
  let n = deg % 360;
  if (n < 0) n += 360;
  if (n === 0) return { c: 1, s: 0 };
  if (n === 90) return { c: 0, s: 1 };
  if (n === 180) return { c: -1, s: 0 };
  if (n === 270) return { c: 0, s: -1 };
  const rad = (n * Math.PI) / 180;
  return { c: Math.cos(rad), s: Math.sin(rad) };
}

/**
 * Rotate a plan vector by a three.js yaw (counter-clockwise about +Y when
 * looking down from +Y): x' = x·cosθ + z·sinθ, z' = −x·sinθ + z·cosθ.
 */
export function rotatePlanVector(v: PlanPoint, rotationYDeg: number): PlanPoint {
  const { c, s } = cosSinDeg(rotationYDeg);
  return { x: v.x * c + v.z * s, z: -v.x * s + v.z * c };
}

/**
 * World-axis-aligned extents of the rotated footprint (i.e. the plan size of
 * the world AABB that encloses the module after a yaw of `rotationYDeg`).
 */
export function rotatedPlanExtents(size: PlanSize, rotationYDeg: number): PlanSize {
  const { c, s } = cosSinDeg(rotationYDeg);
  return {
    w: Math.abs(size.w * c) + Math.abs(size.d * s),
    d: Math.abs(size.w * s) + Math.abs(size.d * c),
  };
}

/**
 * World-axis offset from the footprint CENTRE to the anchor point, for a
 * module of local plan size `size` rotated by `rotationYDeg` about its
 * centre. The centre is rotation-invariant, so this is just the rotated
 * local anchor vector.
 */
export function anchorOffsetFromCenter(
  anchor: AnchorPointId,
  size: PlanSize,
  rotationYDeg: number,
): PlanPoint {
  const f = ANCHOR_FRACTIONS[anchor];
  return rotatePlanVector(
    { x: (f.fx - 0.5) * size.w, z: (f.fz - 0.5) * size.d },
    rotationYDeg,
  );
}

/**
 * World-axis offset from the module's MIN-CORNER (the min corner of the
 * world AABB of the rotated footprint) to the anchor point.
 *
 * For rotation 0 the min-corner is the local (left, back) corner; for other
 * yaws the world AABB grows/swaps, and the anchor (a physical point on the
 * module) travels with the geometry.
 */
export function anchorOffset(
  anchor: AnchorPointId,
  size: PlanSize,
  rotationYDeg: number,
): PlanPoint {
  const ext = rotatedPlanExtents(size, rotationYDeg);
  const fromCenter = anchorOffsetFromCenter(anchor, size, rotationYDeg);
  return { x: ext.w * 0.5 + fromCenter.x, z: ext.d * 0.5 + fromCenter.z };
}

/**
 * Convert a pointer (world XZ position that the ACTIVE anchor should land
 * on) to the module's min-corner position. Inverse of
 * `minCorner + anchorOffset = anchorPoint`.
 */
export function pointerToMinCorner(
  pointerWorld: PlanPoint,
  anchor: AnchorPointId,
  size: PlanSize,
  rotationYDeg: number,
): PlanPoint {
  const off = anchorOffset(anchor, size, rotationYDeg);
  return { x: pointerWorld.x - off.x, z: pointerWorld.z - off.z };
}

/** Tab — next anchor in the cycle (wraps). */
export function nextAnchorPoint(anchor: AnchorPointId): AnchorPointId {
  const i = ANCHOR_POINT_IDS.indexOf(anchor);
  return ANCHOR_POINT_IDS[(i + 1) % ANCHOR_POINT_IDS.length]!;
}

/** Shift+Tab — previous anchor in the cycle (wraps). */
export function prevAnchorPoint(anchor: AnchorPointId): AnchorPointId {
  const i = ANCHOR_POINT_IDS.indexOf(anchor);
  return ANCHOR_POINT_IDS[(i - 1 + ANCHOR_POINT_IDS.length) % ANCHOR_POINT_IDS.length]!;
}
